import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DialogRef } from 'src/app/common/Forms/Dialog-types/dialog-ref';
import { IProject } from 'src/app/common/Forms/Dialogs/import-scans-dialog/import-scans-util';
import { NotificationDialogComponent } from 'src/app/common/Forms/Dialogs/notification-dialog/notification-dialog.component';
import { InputsBindingsModel } from 'src/app/common/Models/Dialog/inputs-binding.model';
import { ISphereXgConfig, IPointCloudSession, PointCloudProvider } from 'src/app/common/Models/UI/point-cloud.interface';
import { ButtonInfo } from 'src/app/common/UI-Components/helperClasses/value-and-display.class';
import { RemovePointCloudSession, SetCompanyInfo, SetPointCloudSession } from 'src/app/Store/app.state/app.actions';
import { ILayer, IScanProjectProperties } from 'src/app/Store/layers.state/layers.model';
import { IPlacemark } from 'src/app/Store/placemarks.state/placemarks.model';
import { environment } from 'src/environments/environment';
import { DialogService, DialogType } from '../dialogs.service';
import { SiemensAnalyticsService } from '../siemens-analytics.service';
import { StatusService, MessagesBank } from '../status.service';
import { ServerApi } from './server.api';
import { PointCloudConfDialogComponent } from 'src/app/common/Forms/Dialogs/point-cloud-conf-dialog/point-cloud-conf-dialog.component';
import { SCENE_MODE } from 'src/app/Store/app.state/app.model';
import { ICompanyInfo } from 'src/app/common/Models/UI/company-info.interface';
import { AppState } from 'src/app/Store/app.state/app.state';
import { ISite } from 'src/app/Store/sites.state/sites.model';
import { DialogModel } from 'src/app/common/Models/Dialog/dialog.model';
import { SessionApiSvc } from './session.api.svc';
import { CMD_ACTIONS, CMD_TARGETS, CmdRouterService } from '../cmd-router.service';

interface CameraParameters {
  cameraMode: string;
  near: number;
  far: number;
  posX: number;
  posY: number;
  posZ: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  fov: number;
}

@Injectable({
  providedIn: 'root'
})
export class SphereXGApiService {
  private readonly sphereXGLoginInfo = new Subject<any>();

  public companyInfo: ICompanyInfo = null;
  companyInfo$: Observable<ICompanyInfo>;
  constructor(private readonly serverApi: ServerApi,
    private readonly statusBar: StatusService,
    private readonly store: Store,
    private readonly sessionApiSvc: SessionApiSvc,
    private readonly dialogSrv: DialogService,
    private readonly siemensAnalyticsService: SiemensAnalyticsService,
    private readonly dialogService: DialogService,
    private readonly cmdRouterSvc: CmdRouterService) {
    this.companyInfo$ = this.store.select(AppState.getCompanyInfo);
    this.companyInfo$.subscribe( (companyInfo: ICompanyInfo) => {
        this.companyInfo = companyInfo;
      });
    }

  public pointCloudConfsCreated(object: any, throughSse: boolean): void {
    const createConf: ISphereXgConfig = throughSse ? object.sphereXgSaConf : object;
    const sphereXGConfs = this.companyInfo.sphereXGConfs;
    sphereXGConfs.push(createConf);
    this.companyInfo.sphereXGConfs = sphereXGConfs;
    this.store.dispatch(new SetCompanyInfo(this.companyInfo));
  }

  public pointCloudConfsDeleted(object: any, throughSse: boolean): void {
    const deletedConf: ISphereXgConfig = throughSse ? object.sphereXgSaConf : object;
    const activeSite: ISite = this.store.selectSnapshot<ISite>((state: any) => state.StateApp.activeSite );
    const sphereXGConfs = this.companyInfo.sphereXGConfs;
    const index: number = sphereXGConfs.findIndex((conf: ISphereXgConfig) => conf.id === deletedConf.id);
    if (index !== -1) {
      sphereXGConfs.splice(index, 1);
    }
    if (activeSite.pointCloudConfId === deletedConf.id) {
      this.pointCloudConfUpdatedForActiveSite(throughSse);
    }
    this.companyInfo.sphereXGConfs = sphereXGConfs;
    this.store.dispatch(new SetCompanyInfo(this.companyInfo));
  }

  public pointCloudConfsUpdated(object: any, throughSse: boolean): void {
    const updatedConf: ISphereXgConfig = throughSse ? object.sphereXgSaConf : object;
    const activeSite: ISite = this.store.selectSnapshot<ISite>((state: any) => state.StateApp.activeSite );
    const sphereXGConfs = this.companyInfo.sphereXGConfs;
    const index: number = sphereXGConfs.findIndex((conf: ISphereXgConfig) => conf.id === updatedConf.id);
    if (index !== -1) {
      sphereXGConfs[index] = updatedConf;
    } else {
      sphereXGConfs.push(updatedConf);
    }

    if (activeSite.pointCloudConfId === updatedConf.id) {
      this.pointCloudConfUpdatedForActiveSite(throughSse);
    }
    this.companyInfo.sphereXGConfs = sphereXGConfs;
    this.store.dispatch(new SetCompanyInfo(this.companyInfo));
  }

  public pointCloudConfUpdatedForActiveSite(throughSse: boolean): void {
    if (throughSse) {
      const inputsBinding: InputsBindingsModel = new Map([
        ['type', 'warning'],
        ['title', 'Point Cloud Configuration Changes'],
        ['message', `The administrator has changed the point cloud configuration method in your site.
        You must login again for the modifications to become active.<br/> Press Continue to return to Login.`]
      ]);
      const dialog: DialogRef = this.dialogSrv.createNotificationDialog(inputsBinding);
      const dialogComp: NotificationDialogComponent = (dialog.instance as NotificationDialogComponent);

      dialogComp.buttonsInfo = [
        new ButtonInfo('ok', 'Continue'),
      ];
      dialog.onClose$().subscribe((model: DialogModel) => {
        if (model.userAction === 'ok') {
          this.sessionApiSvc.closeSession();
        }
      });
    } else {
      this.clearPointCloudSession();
    }
  }

  public pointCloudLoginInfoInternal(): Observable<any> {
    return this.sphereXGLoginInfo.asObservable();
  }

  public getProjects(): Observable<any> {
    const getProjectsUrl: string = `${environment.serverUrl}/newapi/external/projects`;
    return this.serverApi.sendGetToServer(getProjectsUrl).pipe(
      map((response: any) => response.data.map((data: any) => ({
      ...data.project,
      displayName: data.project.name
      })))
    );
  }

  public getScans(project: IProject): Observable<any> {
    const getScansUrl: string = `${environment.serverUrl}/newapi/external/${project.id}/query?types=Section&typeHints=FocusScan,ScanWsVirtual,ScanWs`;
    return this.serverApi.sendGetToServer(getScansUrl).pipe(
      map((response: any) => response.page.map((data: any) => ({
        ...data,
        x: data.globalPose.pos.x,
        y: data.globalPose.pos.z,
        z: data.globalPose.pos.y
        })))
    );
  }

  public async getPointCloudId(selectedProject: any): Promise<any> {
    try {
      const pointCloudStreamUrl = environment.serverUrl + `/newapi/external/${selectedProject.id}/query?types=PointCloudStream&typeHints=none`;
      const result = await this.serverApi.sendGetToServer(pointCloudStreamUrl).toPromise();
      return result.page[0]?.id ?? null;
    } catch(err) {
      this.statusBar.removeStatus(MessagesBank.IMPORTING_SCAN);
      this.serverApi.createNotifiactionDialogForHttpCrisis(err, 'Error in getting Point Cloud. Please try later');
    }
  }

  public async loginToPointCloud(): Promise<any> {
    const pointCloudSession: IPointCloudSession = this.store.selectSnapshot<IPointCloudSession>((state: any) => state.StateApp.pointCloudSession );
    if (pointCloudSession) {
      this.sphereXGLoginInfo.next(true);
      return;
    }
    const url: string = `${environment.serverUrl}/newapi/external/login`;
    this.statusBar.addNewStatus(MessagesBank.POINTCLOUD_LOGIN_INPROGRESS);
    this.serverApi.sendPostToServer(url, null).subscribe({
      next: (res: any) => {
        this.sphereXGLoginInfo.next(true);
        const sphereXGConfig: IPointCloudSession = {
          domainUrl: res.domain
        }
        localStorage.setItem('pointCloudSession', JSON.stringify(sphereXGConfig));
        this.store.dispatch(new SetPointCloudSession(sphereXGConfig));
        this.statusBar.removeStatus(MessagesBank.POINTCLOUD_LOGIN_INPROGRESS);
      },
      error: (err: HttpErrorResponse) => {
        if ( err.error?.errorMessage) {
          const inputsBinding: InputsBindingsModel = new Map([
            [ 'type', 'err'],
            [ 'title', `Loading Error` ],
            [ 'message', err.error.errorMessage]
          ]);
          const dialog: DialogRef = this.dialogService.createNotificationDialog(inputsBinding);
          const dialogComp: NotificationDialogComponent = (dialog.instance as NotificationDialogComponent);
          dialogComp.buttonsInfo = [new ButtonInfo('close', 'Close')];
        } else {
          this.serverApi.createNotifiactionDialogForHttpCrisis(err, "There was an internal error. Please contact the System Administrator.");
        }
        this.sphereXGLoginInfo.next(false);
        this.statusBar.removeStatus(MessagesBank.POINTCLOUD_LOGIN_INPROGRESS);
      }
    });
  }

  public cancelCheckConnection(): void {
    this.statusBar.removeStatus(MessagesBank.POINT_CLOUD_CHECK_CONNECTION_INPROGRESS);
  }
  
  public async showPointCloud(sceneMode: SCENE_MODE, scanProjectProperties: IScanProjectProperties, scanId: string, phi: number, theta: number, fov: number) {
    const placemarks: IPlacemark[] = this.store.selectSnapshot<IPlacemark[]>((state: any) => state.StatePlacemarks.placemarks);
    const placemark: IPlacemark = placemarks.find( pm => pm.scanId == scanId);
    
    let projectId = scanProjectProperties?.id;
    if (!projectId) {
      const layers: ILayer[] = this.store.selectSnapshot<ILayer[]>((state: any) => state.StateLayers.layers);
      const currentLayer = layers.find((foundLayer: ILayer) => foundLayer.id === placemark.parentLayerId);
      if (!scanProjectProperties) {
        scanProjectProperties = currentLayer.scanProjectProperties;
      }
      projectId = scanProjectProperties?.id;
    }
    try {
      this.statusBar.addNewStatus(MessagesBank.OPENING_OPINTCLOUD);
      if (sceneMode === SCENE_MODE.Facility) {
        await this.checkUserAccessToProject(placemark.parentLayerId);
      }
      this.getFocusScan(projectId, scanId).subscribe( async (loadedFocusScan: any) => {
        const pointCloudSession: IPointCloudSession = this.store.selectSnapshot<IPointCloudSession>((state: any) => state.StateApp.pointCloudSession );
        const pointCloudUrl = pointCloudSession ? pointCloudSession.domainUrl.replace(/^api/, 'viewer') : 'viewer.holobuilder.eu';
        const globalPosition = loadedFocusScan.globalPose.pos;
        globalPosition.z = -globalPosition.z;
        const txyz = this.calculateTargetPoint(globalPosition.x, globalPosition.y, globalPosition.z, theta, phi);
        this.openPointCloud(globalPosition, txyz, fov, pointCloudUrl, projectId, scanProjectProperties?.pointCloudId);
        this.statusBar.removeStatus(MessagesBank.OPENING_OPINTCLOUD);
      });
    } catch (error) {
      console.error('Error during point cloud login:', error);
      this.statusBar.removeStatus(MessagesBank.OPENING_OPINTCLOUD);
    }
  }

  private openPointCloud(globalPosition: any, txyz: any, fov: number, pointCloudUrl: string, projectId: string, pointCloudId: any) {
    const cameraParams: CameraParameters = {
      cameraMode: "P",
      near: 0.1,
      far: 1000,
      posX: globalPosition.x,
      posY: globalPosition.y,
      posZ: globalPosition.z,
      targetX: txyz.tx,
      targetY: txyz.ty,
      targetZ: txyz.tz,
      fov: fov
    };

    const camera = this.getCameraParameterForDeepLink(cameraParams);
    const deeplink = `https://${pointCloudUrl}/project/${projectId}?id=${pointCloudId}&mode=walk&camera=${camera}&scene=pc`;
    this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.PANORAMIC_MANAGER, CMD_ACTIONS.OPEN_POINT_CLOUD_VIEWER, {pointCloudUrl: deeplink});
    this.siemensAnalyticsService.logEvent('INS_PointCloudAccess');
  }

  public getFocusScan(projectId, scanId): Observable<any> {
    const focusScanUrl = `${environment.serverUrl}/newapi/external/${projectId}/ielements/${scanId}`;
    return this.serverApi.sendGetToServer(focusScanUrl);
  }

  public calculateTargetPoint(x, y, z, theta: number, phi: number): any {
    phi += Math.PI;
    const sinTheta = Math.sin(theta);
    const cosTheta = Math.cos(theta);
    const sinPhi = Math.sin(phi);
    const cosPhi = Math.cos(phi);

    const dx = cosTheta * cosPhi;
    const dy = cosTheta * sinPhi;
    const dz = sinTheta;

    const tx = x + dx;
    const ty = y + dz;
    const tz = z + dy;

    return { tx, ty, tz };
  }

  private getCameraParameterForDeepLink(params: CameraParameters): string {
    const { cameraMode, ...coords } = params;
    const parameters = Object.values(coords);
    const encodedParameters = parameters.map(param => this.getEncodedNumberBase36(param));
    const cameraParameter = `${cameraMode}_${encodedParameters.join('_')}`;
    return cameraParameter;
  }

  private getEncodedNumberBase36(valueToEncode: number): string {
    const chars = "0123456789abcdefghijklmnopqrstuvwxyz";
    const CAMERA_ENCODING_PRECISION_FACTOR = 1000;
    let result = '';
    let isNegative = valueToEncode < 0;
    valueToEncode = Math.abs(valueToEncode) * CAMERA_ENCODING_PRECISION_FACTOR;

    do {
        result = chars[Math.floor(valueToEncode % 36)] + result;
        valueToEncode = Math.floor(valueToEncode / 36);
    } while (valueToEncode >= 1);
    return isNegative ? '-' + result : result;
  }

  public async checkUserAccessToProject(layerId: string, scanProjectProperties?: IScanProjectProperties): Promise<any> {
    return new Promise( (resolve, reject) => {
      let projectId = scanProjectProperties?.id;
      let projectName = scanProjectProperties?.name;
      if (!projectId) {
        const layers: ILayer[] = this.store.selectSnapshot<ILayer[]>((state: any) => state.StateLayers.layers);
        const currentLayer = layers.find((foundLayer: ILayer) => foundLayer.id === layerId);
        scanProjectProperties = currentLayer.scanProjectProperties;
        projectId = currentLayer.scanProjectProperties?.id;
        projectName = currentLayer.name;
      }
 
      if (scanProjectProperties.pointCloudProvider === PointCloudProvider.SphereXG) {
        this.checkProjectAccess(projectId)
          .subscribe({
            next: (project: any) => {
              if (project) {
                resolve(true);
              }
            },
            error: (errorResponse: any) => {
              const err = new Error(`You do not have access to the ${projectName} project in SphereXG.`);
              this.serverApi.createNotifiactionDialogForHttpCrisis(err,`You do not have access to the ${projectName} project in SphereXG.`);
              reject(err);
            }
          });
      } else {
        const err = new Error(`You do not have access to the ${projectName} project in SphereXG.`);
          this.serverApi.createNotifiactionDialogForHttpCrisis(err,`You do not have access to the ${projectName} project in SphereXG.`);
          reject(err);
      }
    });
  }

  public checkProjectAccess(projectId: string): Observable<any> {
    const getProjectsUrl: string = `${environment.serverUrl}/newapi/external/${projectId}/access-token`;
    return this.serverApi.sendPostToServer(getProjectsUrl, null);
  }

  public getScanPlacemarkUrl(scanProjectProperties: IScanProjectProperties, scanId: string): string {
    let pointCloudSession: IPointCloudSession = this.store.selectSnapshot<IPointCloudSession>((state: any) => state.StateApp.pointCloudSession );
    const pointCloudUrl = pointCloudSession ? pointCloudSession.domainUrl.replace(/^api/, 'viewer') : 'viewer.holobuilder.eu';
    return `https://${pointCloudUrl}/project/${scanProjectProperties?.id}?id=${scanId}`;
  }

  public getResoultionOptions(): ButtonInfo[] {
    const resolutions: ButtonInfo[] = [
      new ButtonInfo("high", 'High Resolution'),
      new ButtonInfo("low", 'Low Resolution'),
    ];
    return resolutions;
  }

  public getDefaultResolution(): string {
    return "high";
  }

  public openWebPointCloudConfDialog(): void {
    this.dialogService.createDialog(PointCloudConfDialogComponent, DialogType.Modal, null, null, 985);
  }

  public getAllPointCloudConfs() {
    return this.companyInfo.sphereXGConfs;
  }

  public getPointCloudConfsSelectionOpts(): any[] {
    const pointCloudConfs = this.companyInfo.sphereXGConfs || [];
    const pointCloudConfOptions = [];
    pointCloudConfs.forEach( (webshareConf: ISphereXgConfig) => pointCloudConfOptions.push(new ButtonInfo(webshareConf.id, webshareConf.name)));
    return pointCloudConfOptions;
  }

  public getPointCloudConfById(pointCloudConfId: number): ISphereXgConfig | null {
    const pointCloudConfs = this.companyInfo.sphereXGConfs || [];
    const webshareConf = pointCloudConfs.find( (conf: ISphereXgConfig) => conf.id == pointCloudConfId) || null;
    return webshareConf;
  }

  getSphereXgSaConfigs(): Observable<ISphereXgConfig> {
    const url: string = `${environment.serverUrl}/newapi/pointcloud/service-account-confs`;
    return this.serverApi.sendGetToServer(url);
  }

  removeServiceAccountConfig(confId: number): Observable<any> {
    const url: string = `${environment.serverUrl}/newapi/pointcloud/service-account-confs/${confId}`;
    return this.serverApi.sendDeleteToServer(url);
  }

  updateServiceAccountConfig(sphereXgSaConfig: ISphereXgConfig): Observable<ISphereXgConfig> {
    const url: string = `${environment.serverUrl}/newapi/pointcloud/service-account-confs/${sphereXgSaConfig.id}`;
    return this.serverApi.sendPutToServer(url, sphereXgSaConfig);
  }

  addServiceAccountConfig(sphereXgSaConfig: ISphereXgConfig): Observable<ISphereXgConfig> {
    const url: string = `${environment.serverUrl}/newapi/pointcloud/service-account-confs`;
    return this.serverApi.sendPostToServer(url, sphereXgSaConfig);
  }

  async checkConnection(sphereXgSaConfig: ISphereXgConfig): Promise<any> {
    this.statusBar.addNewStatus(MessagesBank.POINT_CLOUD_CHECK_CONNECTION_INPROGRESS);
    const url: string = `${environment.serverUrl}/newapi/external/service-account-check`;
    return await this.serverApi.sendPostToServer(url, sphereXgSaConfig).toPromise();
  }

  public setPointCloudSession(pointCloudSession: IPointCloudSession): void {
    pointCloudSession.partnerName = 'FARO';
    localStorage.setItem('pointCloudSession', JSON.stringify(pointCloudSession));
    this.store.dispatch(new SetPointCloudSession(pointCloudSession));
  }

  public clearPointCloudSession(): void {
    this.store.dispatch(new RemovePointCloudSession());
    localStorage.removeItem('pointCloudSession');
  }

  public showTestPointCloudConnection(pointCloudConf: ISphereXgConfig): boolean {
    return !!pointCloudConf;
  }

  public async testPointCloudConnection(pointCloudConf: ISphereXgConfig, showSuccessMsg = true): Promise<void> {
    try {
      const res = await this.checkConnection(pointCloudConf);
      this.statusBar.removeStatus(MessagesBank.POINT_CLOUD_CHECK_CONNECTION_INPROGRESS);
      if (showSuccessMsg) {
        const inputsBinding: InputsBindingsModel = new Map([
          [ 'type', 'info'],
          [ 'title', `Connection is successful` ],
          [ 'message', res.successMessage]
        ]);
        const dialog: DialogRef = this.dialogService.createNotificationDialog(inputsBinding);
        const dialogComp: NotificationDialogComponent = (dialog.instance as NotificationDialogComponent);
        dialogComp.buttonsInfo = [new ButtonInfo('close', 'Close')];
      }
    } catch (err) {
      if ( err.error?.errorMessage) {
        const inputsBinding: InputsBindingsModel = new Map([
          [ 'type', 'err'],
          [ 'title', `Loading Error` ],
          [ 'message', err.error.errorMessage]
        ]);
        const dialog: DialogRef = this.dialogService.createNotificationDialog(inputsBinding);
        const dialogComp: NotificationDialogComponent = (dialog.instance as NotificationDialogComponent);
        dialogComp.buttonsInfo = [new ButtonInfo('close', 'Close')];
      } else {
        this.serverApi.createNotifiactionDialogForHttpCrisis(err, "There was an internal error. Please contact the System Administrator.");
      }
      this.statusBar.removeStatus(MessagesBank.POINT_CLOUD_CHECK_CONNECTION_INPROGRESS);
      throw err;
    }
  }

  public getPointCloudConfIdFromSite(site: any): number {
    return site.pointCloudConfId ?? 0;
  }

  public setPointCloudConfIdInSite(site: any): void {
    //pointCloudConfId is already there in pointCloudConfId field of the site object, so 
    //no need to set it again
    console.log('setPointCloudConfIdInSite', site);
  }
}
